import { debounce } from '../../core/utils/debounce.js';
import { preserveGrowthCorrelation } from '../../core/utils/params.js';
import { preserveConsentedGoogleAdsAttribution } from '../../core/growth/googleAdsAttribution.js';

const DEFAULT_QUESTIONS = [
    {
        id: 'homeType',
        key: 'homeType',
        title: 'Which best describes your home?',
        subtitle: 'This helps us estimate access and scaffolding.',
        type: 'single',
        options: [
            { value: 'house', label: 'House', sub: 'Two or more storeys' },
            { value: 'bungalow', label: 'Bungalow', sub: 'Single storey' },
            { value: 'flat', label: 'Flat', sub: 'We will need to chat first' }
        ]
    },
    {
        id: 'occupancy',
        key: 'occupancy',
        title: 'Are you a homeowner, landlord, or tenant?',
        type: 'single',
        options: [
            { value: 'homeowner', label: 'Homeowner' },
            { value: 'landlord', label: 'Landlord' },
            { value: 'tenant', label: 'Tenant' }
        ]
    },
    {
        id: 'usageTiming',
        key: 'usageTiming',
        title: 'When do you mostly use electricity?',
        subtitle: 'This helps us estimate savings.',
        type: 'single',
        options: [
            { value: 'all_day', label: 'All day' },
            { value: 'half_day', label: 'Half the day' }
        ]
    },
    {
        id: 'usageKnown',
        key: 'usageKnown',
        title: 'Do you know your annual electricity use?',
        type: 'single',
        options: [
            { value: 'no', label: 'No, estimate it', next: 'usageProfile' },
            { value: 'yes', label: 'Yes, I have the figure', next: 'annualUsage_kWh' }
        ]
    },
    {
        id: 'usageProfile',
        key: 'usageProfile',
        title: "Describe your home's electricity use",
        subtitle: 'We will estimate a typical annual usage.',
        type: 'single',
        options: [
            { value: 'low', label: 'Low', sub: 'We are careful' },
            { value: 'average', label: 'Fairly average' },
            { value: 'high', label: 'High', sub: 'We use a lot' },
            { value: 'very_high', label: 'Very high', sub: 'Heat pump and/or EVs' }
        ]
    },
    {
        id: 'annualUsage_kWh',
        key: 'annualUsage_kWh',
        title: 'Enter your annual usage (kWh)',
        subtitle: 'Typical homes are between 2,000 and 6,000 kWh per year.',
        type: 'number'
    }
];

const USAGE_PROFILES = {
    low: 2200,
    average: 3300,
    high: 4500,
    very_high: 6000
};

function normalizeLatLng(point) {
    if (!point || typeof point !== 'object') return null;
    const lat = point.lat ?? point.latitude;
    const lon = point.lon ?? point.lng ?? point.longitude;
    if (typeof lat !== 'number' || typeof lon !== 'number') return null;
    return { lat, lon };
}

function normalizePolygon(points) {
    if (!Array.isArray(points)) return null;
    const normalized = points.map(normalizeLatLng).filter(Boolean);
    return normalized.length >= 3 ? normalized : null;
}

function polygonFromBoundingBox(box) {
    if (!box || typeof box !== 'object') return null;
    const sw = normalizeLatLng(box.southwest ?? box.southWest ?? box.sw);
    const ne = normalizeLatLng(box.northeast ?? box.northEast ?? box.ne);
    if (!sw || !ne) return null;
    return [
        { lat: sw.lat, lon: sw.lon },
        { lat: sw.lat, lon: ne.lon },
        { lat: ne.lat, lon: ne.lon },
        { lat: ne.lat, lon: sw.lon }
    ];
}

function normalizeKey(input) {
    return String(input || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function isUsageKnownQuestion(question) {
    const keyId = normalizeKey(question?.key || question?.id);
    if (keyId === 'usageknown') return true;
    const options = question?.options || [];
    if (!options.length) return false;
    const values = options.map(opt => normalizeKey(opt.value));
    return values.includes('yes') && values.includes('no') && keyId.includes('usage');
}

function isUsageProfileQuestion(question) {
    const keyId = normalizeKey(question?.key || question?.id);
    if (keyId === 'usageprofile') return true;
    const options = question?.options || [];
    if (!options.length) return false;
    const values = options.map(opt => normalizeKey(opt.value));
    return values.some(value => Object.keys(USAGE_PROFILES).includes(value));
}

function isAnnualUsageQuestion(question) {
    const keyId = normalizeKey(question?.key || question?.id);
    if (keyId === 'annualusagekwh' || keyId === 'annualusage') return true;
    const type = String(question?.type || '').toLowerCase();
    if (type !== 'number') return false;
    const title = normalizeKey(question?.title || '');
    return title.includes('usage') || title.includes('electricity');
}

function extractSegments(googleSolarData) {
    const stats = googleSolarData?.solarPotential?.roofSegmentStats ?? [];
    return stats.map((segment, index) => {
        const polygon =
            normalizePolygon(segment.polygon) ||
            normalizePolygon(segment?.segment?.polygon) ||
            polygonFromBoundingBox(segment.boundingBox) ||
            polygonFromBoundingBox(segment?.segment?.boundingBox);
        const area =
            segment.areaMeters2 ??
            segment.area_m2 ??
            segment.stats?.areaMeters2 ??
            segment.stats?.area_m2 ??
            null;
        return {
            id: segment.segmentIndex ?? segment.id ?? index,
            pitchDeg: segment.pitchDegrees ?? segment.pitchDeg ?? segment.pitch ?? null,
            azimuthDeg: segment.azimuthDegrees ?? segment.azimuthDeg ?? segment.azimuth ?? null,
            area_m2: typeof area === 'number' ? area : null,
            sunshineScore: segment.stats?.sunshineQuantiles?.[2] ?? segment.stats?.sunshineScore ?? 1,
            polygon
        };
    });
}

function buildQuestionFlow(questionSet) {
    const source = Array.isArray(questionSet?.questions) ? questionSet.questions : DEFAULT_QUESTIONS;
    const questions = source.map((q, idx) => normalizeQuestion(q, idx)).filter(Boolean);
    const map = new Map();
    const order = [];
    for (const q of questions) {
        map.set(q.id, q);
        order.push(q.id);
    }
    return {
        list: questions,
        map,
        order,
        firstId: order[0] ?? null
    };
}

function normalizeQuestion(raw, index) {
    if (!raw || typeof raw !== 'object') return null;
    const id = String(raw.id ?? raw.key ?? raw.attribute ?? raw.slug ?? `q${index + 1}`);
    const key = String(raw.key ?? raw.attribute ?? raw.slug ?? id);
    const title = raw.title ?? raw.label ?? raw.question ?? raw.friendly_name ?? 'Question';
    const subtitle = raw.subtitle ?? raw.help ?? '';
    const answers = raw.options ?? raw.answers ?? raw.choices ?? [];
    const rawType = raw.type ?? raw.inputType ?? (Array.isArray(answers) && answers.length ? 'single' : 'single');
    const normalizedType = String(rawType || '').toLowerCase();
    const type = ['number', 'numeric', 'integer'].includes(normalizedType) ? 'number' : rawType;
    const options = Array.isArray(answers)
        ? answers.map((opt, idx2) => ({
            value: opt.value ?? opt.id ?? opt.code ?? String(idx2),
            label: opt.label ?? opt.title ?? opt.text ?? String(opt.value ?? opt.id ?? ''),
            sub: opt.sub ?? opt.hint ?? opt.description ?? '',
            next: opt.next_question_id != null || opt.next != null
                ? String(opt.next_question_id ?? opt.next)
                : null
        }))
        : [];

    const nextValue = raw.next ?? raw.nextByValue ?? null;
    const next = (typeof nextValue === 'string' || typeof nextValue === 'number')
        ? String(nextValue)
        : nextValue;

    return {
        id,
        key,
        title,
        subtitle,
        type,
        options,
        next
    };
}

function getNextQuestionId(question, value, flow) {
    const keyId = normalizeKey(question.key || question.id);
    if (question.options) {
        const option = question.options.find(opt => String(opt.value) === String(value));
        if (option?.next) return option.next;
    }
    if (typeof question.next === 'string') return question.next;
    if (question.next && typeof question.next === 'object' && question.next[value]) {
        return question.next[value];
    }
    if (isUsageKnownQuestion(question) || keyId === 'usageknown') {
        return String(value).toLowerCase() === 'yes' ? 'annualUsage_kWh' : 'usageProfile';
    }
    if (isUsageProfileQuestion(question) || isAnnualUsageQuestion(question)) {
        return 'address';
    }
    const order = flow?.order ?? [];
    const idx = order.indexOf(question.id);
    if (idx >= 0 && idx < order.length - 1) {
        return order[idx + 1];
    }
    return 'address';
}

function createRoofMapController({ store, refs, location, ensureGoogleSolarData }) {
    const state = {
        map: null,
        points: [],
        markers: [],
        polyline: null,
        polygon: null,
        savedPolygons: [],
        isClosed: false,
        pitchDeg: null,
        azimuthDeg: null
    };

    const CLOSE_DISTANCE_M = 1.5;
    const SAVED_COLORS = ['#0f766e', '#2563eb', '#0ea5e9', '#10b981'];

    const mapEl = refs?.map;
    const crosshair = refs?.mapCrosshair;
    const addPointBtn = refs?.mapAdd;
    const undoBtn = refs?.mapUndo;
    const closeBtn = refs?.mapClose;
    const clearBtn = refs?.mapClear;
    const submitBtn = refs?.mapSubmit;
    const manualBtn = refs?.mapManual;
    const note = refs?.mapNote;
    const overlay = refs?.mapOverlay;
    const compassStep = refs?.mapCompassStep;
    const pitchStep = refs?.mapPitchStep;
    const addSegmentBtn = refs?.mapAddSegment;

    if (!mapEl) throw new Error('Missing map element (x-ref="map").');

    const isCoarse = window.matchMedia('(pointer: coarse)').matches;

    function markerIcon(checked) {
        const svg = checked
            ? '<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26"><circle cx="13" cy="13" r="9" fill="#f59e0b" stroke="#0f172a" stroke-width="2"/><path d="M8 13.5l3 3 7-7" fill="none" stroke="#0f172a" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>'
            : '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22"><circle cx="11" cy="11" r="7" fill="#f59e0b" stroke="#0f172a" stroke-width="2"/></svg>';
        return {
            url: `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`,
            scaledSize: new google.maps.Size(22, 22),
            anchor: new google.maps.Point(11, 11)
        };
    }

    function updateNote() {
        if (!note) return;
        if (state.isClosed) {
            note.textContent = 'Nice. Choose direction and slope, then save the segment.';
            if (closeBtn) closeBtn.disabled = true;
            if (undoBtn) undoBtn.disabled = true;
            if (submitBtn) submitBtn.disabled = true;
            if (crosshair) crosshair.hidden = true;
            return;
        }
        if (state.points.length === 0) {
            note.textContent = 'Tap to place the first point.';
        } else if (state.points.length === 1) {
            note.textContent = 'Tap around the edges of the roof.';
        } else {
            note.textContent = "When you're done, tap the first point again to complete the shape.";
        }
        if (closeBtn) closeBtn.disabled = state.points.length < 3 || state.isClosed;
        if (undoBtn) undoBtn.disabled = state.points.length === 0 || state.isClosed;
        if (submitBtn) submitBtn.disabled = state.points.length > 0 || state.isClosed;
        if (crosshair) crosshair.hidden = state.points.length > 0;
    }

    function resetAngleSelection() {
        state.pitchDeg = null;
        state.azimuthDeg = null;
        if (overlay) {
            overlay.querySelectorAll('[data-pitch]').forEach(btn => btn.classList.remove('is-active'));
            overlay.querySelectorAll('[data-azimuth]').forEach(btn => btn.classList.remove('is-active'));
        }
        store.roofOverlayVisible = false;
        store.roofCompassVisible = true;
        store.roofPitchVisible = false;
    }

    function clearDrawing() {
        state.points = [];
        state.markers.forEach(marker => marker.setMap(null));
        state.markers = [];
        if (state.polyline) state.polyline.setMap(null);
        if (state.polygon) state.polygon.setMap(null);
        state.polyline = null;
        state.polygon = null;
        state.isClosed = false;
        store.roofOverlayVisible = false;
        resetAngleSelection();
        updateNote();
        updateToolbarState();
    }

    function updatePolyline() {
        if (!state.map || state.isClosed) return;
        if (state.polyline) state.polyline.setMap(null);
        state.polyline = new google.maps.Polyline({
            map: state.map,
            path: state.points,
            strokeColor: '#f59e0b',
            strokeWeight: 2
        });
    }

    function refreshMarkerIcons() {
        state.markers.forEach((marker, idx) => {
            marker.setIcon(markerIcon(state.isClosed && idx === state.markers.length - 1));
        });
    }

    function addPoint(latLng) {
        if (state.isClosed) return;
        state.points.push(latLng);
        const marker = new google.maps.Marker({
            position: latLng,
            map: state.map,
            clickable: true,
            icon: markerIcon(false)
        });
        state.markers.push(marker);
        if (state.points.length === 1) {
            marker.addListener('click', () => {
                if (state.points.length >= 3 && !state.isClosed) {
                    closePolygon();
                }
            });
        }
        refreshMarkerIcons();
        updatePolyline();
        updateNote();
        updateToolbarState();
    }

    function isNearFirstPoint(latLng) {
        if (!state.points.length || !google?.maps?.geometry?.spherical) return false;
        const first = state.points[0];
        const distance = google.maps.geometry.spherical.computeDistanceBetween(latLng, first);
        return distance <= CLOSE_DISTANCE_M;
    }

    function handleMapPoint(latLng) {
        if (state.isClosed) return;
        if (state.points.length >= 3 && isNearFirstPoint(latLng)) {
            closePolygon();
            return;
        }
        addPoint(latLng);
    }

    function undoPoint() {
        if (state.isClosed) return;
        const marker = state.markers.pop();
        if (marker) marker.setMap(null);
        state.points.pop();
        updatePolyline();
        refreshMarkerIcons();
        updateNote();
        updateToolbarState();
    }

    async function closePolygon() {
        if (state.points.length < 3 || state.isClosed) return;
        if (state.polyline) state.polyline.setMap(null);
        state.polygon = new google.maps.Polygon({
            map: state.map,
            paths: state.points,
            fillColor: '#f59e0b',
            fillOpacity: 0.2,
            strokeColor: '#f59e0b',
            strokeWeight: 2
        });
        state.isClosed = true;
        refreshMarkerIcons();
        updateNote();
        updateToolbarState();

        store.roofOverlayVisible = true;
        store.roofCompassVisible = true;
        store.roofPitchVisible = false;

        if (typeof ensureGoogleSolarData === 'function') {
            Promise.resolve(ensureGoogleSolarData(location.lat, location.lon))
                .catch(() => {});
        }
    }

    function updateToolbarState() {
        const hasSegments = store.roofSegments.length > 0;
        const shouldHide = !hasSegments;
        store.roofPostSegmentVisible = hasSegments && state.points.length === 0 && !state.isClosed;
        if (addSegmentBtn) {
            addSegmentBtn.disabled = !hasSegments || state.points.length > 0 || state.isClosed;
            addSegmentBtn.hidden = shouldHide;
        }
        if (submitBtn) {
            submitBtn.disabled = !hasSegments || state.points.length > 0 || state.isClosed;
            submitBtn.hidden = shouldHide;
        }
    }

    function renderSavedPolygons() {
        state.savedPolygons.forEach(poly => poly.setMap(null));
        state.savedPolygons = [];
        store.roofSegments.forEach((segment, idx) => {
            if (!segment?.polygon?.length) return;
            const color = SAVED_COLORS[idx % SAVED_COLORS.length];
            const poly = new google.maps.Polygon({
                map: state.map,
                paths: segment.polygon.map(p => ({ lat: p.lat, lng: p.lon })),
                fillColor: color,
                fillOpacity: 0.18,
                strokeColor: color,
                strokeWeight: 2
            });
            state.savedPolygons.push(poly);
        });
    }

    function saveSegment() {
        if (!Number.isFinite(state.pitchDeg) || !Number.isFinite(state.azimuthDeg)) {
            alert('Choose both direction and slope.');
            return;
        }
        const area_m2 = google.maps.geometry.spherical.computeArea(state.points);
        store.roofSegments.push({
            polygon: state.points.map(p => ({ lat: p.lat(), lon: p.lng() })),
            area_m2,
            pitchDeg: state.pitchDeg,
            azimuthDeg: state.azimuthDeg
        });
        renderSavedPolygons();
        clearDrawing();
        updateToolbarState();
    }

    function getRoofPayload() {
        if (!store.roofSegments.length) return null;
        return {
            location: { lat: location.lat, lon: location.lon },
            planes: store.roofSegments.map(segment => ({
                footprintArea_m2: segment.area_m2,
                pitchDeg: segment.pitchDeg
            })),
            segments: store.roofSegments.map(segment => ({
                area_m2: segment.area_m2,
                pitchDeg: segment.pitchDeg,
                azimuthDeg: segment.azimuthDeg,
                polygon: segment.polygon
            })),
            selection: {
                type: 'manual',
                pitchDeg: store.roofSegments[0].pitchDeg,
                azimuthDeg: store.roofSegments[0].azimuthDeg
            },
            config: { pvgis: { enabled: true } }
        };
    }

    state.map = new google.maps.Map(mapEl, {
        center: { lat: location.lat, lng: location.lon },
        zoom: 20,
        mapTypeId: 'satellite',
        tilt: 0,
        fullscreenControl: false,
        streetViewControl: false,
        mapTypeControl: false
    });

    state.map.addListener('click', (event) => {
        handleMapPoint(event.latLng);
    });

    if (crosshair) crosshair.hidden = true;
    if (addPointBtn) addPointBtn.hidden = true;

    if (overlay) {
        overlay.querySelectorAll('[data-azimuth]').forEach(button => {
            button.addEventListener('click', () => {
                state.azimuthDeg = Number(button.getAttribute('data-azimuth'));
                overlay.querySelectorAll('[data-azimuth]').forEach(el => {
                    el.classList.toggle('is-active', el === button);
                });
                store.roofCompassVisible = false;
                store.roofPitchVisible = true;
            });
        });
        overlay.querySelectorAll('[data-pitch]').forEach(button => {
            button.addEventListener('click', () => {
                state.pitchDeg = Number(button.getAttribute('data-pitch'));
                overlay.querySelectorAll('[data-pitch]').forEach(el => {
                    el.classList.toggle('is-active', el === button);
                });
                saveSegment();
            });
        });
    }

    if (manualBtn) manualBtn.addEventListener('click', () => store.go('roof'));
    if (undoBtn) undoBtn.addEventListener('click', undoPoint);
    if (clearBtn) clearBtn.addEventListener('click', clearDrawing);
    if (closeBtn) closeBtn.addEventListener('click', closePolygon);
    if (addSegmentBtn) addSegmentBtn.addEventListener('click', () => clearDrawing());

    updateNote();
    renderSavedPolygons();
    updateToolbarState();

    return {
        getRoofPayload,
        undoPoint,
        clearDrawing,
        destroy() {
            if (state.polyline) state.polyline.setMap(null);
            if (state.polygon) state.polygon.setMap(null);
            state.markers.forEach(marker => marker.setMap(null));
            state.savedPolygons.forEach(poly => poly.setMap(null));
        }
    };
}

export function registerSolarJourney() {
    if (typeof document === 'undefined') return;

    const createStore = () => {
        if (typeof window !== 'undefined' && window.__solarJourneyState) {
            return window.__solarJourneyState;
        }

        const state = ({
        step: 'intro',
        history: [],
        loading: false,
        error: null,

        jobId: null,
        started: false,
        questionSet: null,
        questionFlow: null,
        currentQuestionId: null,
        answers: {},

        addressQuery: '',
        addressResults: [],
        addressSearching: false,
        address: null,
        selectedAddressUdprn: null,

        energyDemand: null,

        googleSolar: null,
        googleSolarPromise: null,
        googleMapsPromise: null,
        recaptchaPromise: null,
        segments: [],

        roofSegments: [],
        roofResult: null,
        roofConfirmation: null,
        estimateLoading: false,
        estimateLoadingMinMs: 4500,
        loaderState: null,
        roofArea: null,
        roofPitch: null,
        roofAzimuth: null,
        roofOverlayVisible: false,
        roofCompassVisible: true,
        roofPitchVisible: false,
        roofHelpOpen: false,
        roofVideoOpen: false,
        roofCompassVideoOpen: false,
        roofSlopeExplainOpen: false,
        roofPostSegmentVisible: false,
        isTransitioning: false,
        transitionDelayMs: 250,
        transitionTimer: null,
        transitionToken: 0,
        navToken: 0,

        baseUrl: 'https://api.dwellow.co.uk',
        partnerId: null,
        siteKey: '',
        mapsKey: '',
        resultsUrl: '',
        tariffPence: 25,
        resultsPath: '',
        debouncedAddressSearch: null,
        roofMapController: null,

        init() {
            const root = this.$root;
            this.baseUrl = root?.dataset?.apiBase || this.baseUrl;
            this.partnerId = root?.dataset?.partnerId || null;
            this.siteKey = root?.dataset?.siteKey || '';
            this.mapsKey = root?.dataset?.mapsKey || '';
            this.resultsUrl = root?.dataset?.resultsUrl || '';
            this.resultsPath = root?.dataset?.resultsPath || '';
            this.tariffPence = Number(root?.dataset?.tariffPence || 25);
            this.debouncedAddressSearch = debounce(() => this.searchAddress(), 350);
        },

        get currentQuestion() {
            return this.questionFlow?.map.get(this.currentQuestionId) || null;
        },

        get progressPct() {
            const questionCount = this.questionFlow?.order.length ?? DEFAULT_QUESTIONS.length;
            const total = questionCount + (this.roofConfirmation ? 4 : 3);
            let index = 0;
            if (this.step === 'intro') index = 0;
            if (this.step === 'question') {
                const qIndex = Math.max(0, this.questionFlow?.order.indexOf(this.currentQuestionId) ?? 0);
                index = 1 + qIndex;
            }
            if (this.step === 'address') index = 1 + questionCount;
            if (this.step === 'roofMap') index = 2 + questionCount;
            if (this.step === 'roof') index = 3 + questionCount;
            if (this.step === 'confirm') index = 4 + questionCount;
            if (this.step === 'results') index = total;
            const pct = Math.min(100, Math.max(0, Math.round((index / total) * 100)));
            return pct;
        },

        get currentStepIndex() {
            const questionCount = this.questionFlow?.order.length ?? DEFAULT_QUESTIONS.length;
            if (this.step === 'intro') return 1;
            if (this.step === 'question') {
                const qIndex = Math.max(0, this.questionFlow?.order.indexOf(this.currentQuestionId) ?? 0);
                return 2 + qIndex;
            }
            if (this.step === 'address') return 2 + questionCount;
            if (this.step === 'roofMap') return 3 + questionCount;
            if (this.step === 'roof') return 4 + questionCount;
            if (this.step === 'confirm') return 5 + questionCount;
            if (this.step === 'results') return this.totalSteps;
            if (this.step === 'contact') return 2 + questionCount;
            return 1;
        },

        get totalSteps() {
            const questionCount = this.questionFlow?.order.length ?? DEFAULT_QUESTIONS.length;
            return questionCount + (this.roofConfirmation ? 5 : 4);
        },

        go(step, meta = {}, { delayMs = 0 } = {}) {
            if (this.isTransitioning) return;
            if (this.transitionTimer) {
                clearTimeout(this.transitionTimer);
                this.transitionTimer = null;
            }
            const token = ++this.transitionToken;
            const apply = () => {
                if (token !== this.transitionToken) return;
                this.history.push({ step: this.step, questionId: this.currentQuestionId });
                this.step = step;
                if (meta.questionId) {
                    this.currentQuestionId = meta.questionId;
                }
                this.isTransitioning = false;
                if (this.step === 'roofMap') {
                    setTimeout(() => {
                        if (this.step === 'roofMap') this.initRoofMap();
                    }, 0);
                }
            };
            if (delayMs > 0) {
                this.isTransitioning = true;
                this.transitionTimer = setTimeout(() => {
                    this.transitionTimer = null;
                    apply();
                }, delayMs);
                return;
            }
            apply();
        },

        back() {
            if (this.transitionTimer) {
                clearTimeout(this.transitionTimer);
                this.transitionTimer = null;
                this.isTransitioning = false;
            }
            this.transitionToken += 1;
            this.navToken += 1;
            const prev = this.history.pop();
            if (!prev) return;
            if (this.step === 'question') {
                const order = this.questionFlow?.order ?? [];
                const idx = order.indexOf(this.currentQuestionId);
                if (idx > 0) {
                    this.step = 'question';
                    this.currentQuestionId = order[idx - 1];
                    return;
                }
            }
            this.step = prev.step;
            this.currentQuestionId = prev.questionId;
            if (this.step === 'roofMap') {
                setTimeout(() => {
                    if (this.step === 'roofMap') this.initRoofMap();
                }, 0);
            }
        },

        async startJourney(lead = {}) {
            if (this.loading || this.started || this.jobId) return;
            this.loading = true;
            this.error = null;
            try {
                const res = await this.postRecap(`${this.baseUrl}/solar_init`, {
                    partnerId: this.partnerId,
                    lead
                }, 'solar_init');
                this.jobId = res.jobId;
                this.questionSet = res.questionSet;
                this.questionFlow = buildQuestionFlow(res.questionSet);
                this.currentQuestionId = this.questionFlow.firstId;
                this.started = true;
                this.go('question', { questionId: this.currentQuestionId });
            } catch (err) {
                this.error = err.message || 'Unable to start journey.';
            } finally {
                this.loading = false;
            }
        },

        async handleAnswer(value) {
            const question = this.currentQuestion;
            if (!question) return;
            const token = this.navToken;

            const key = question.key;
            if (isUsageProfileQuestion(question)) {
                const estimated = USAGE_PROFILES[value] ?? null;
                this.answers.usageProfile = value;
                if (estimated) {
                    this.answers.annualUsage_kWh = estimated;
                    this.energyDemand = estimated;
                }
            } else if (isAnnualUsageQuestion(question)) {
                const numeric = Number(value);
                this.answers.annualUsage_kWh = numeric;
                this.energyDemand = numeric;
            } else {
                this.answers[key] = value;
            }

            if (key === 'homeType' && value === 'flat') {
                await this.saveAnswers();
                if (token !== this.navToken) return;
                this.go('contact', {}, { delayMs: this.transitionDelayMs });
                return;
            }

            const next = getNextQuestionId(question, value, this.questionFlow);
            if (!next) {
                if (token !== this.navToken) return;
                this.go('address', {}, { delayMs: this.transitionDelayMs });
                return;
            }
            if (this.questionFlow?.map.has(next)) {
                if (token !== this.navToken) return;
                this.go('question', { questionId: next }, { delayMs: this.transitionDelayMs });
                return;
            }
            if (next === 'address') {
                if (token !== this.navToken) return;
                this.go('address', {}, { delayMs: this.transitionDelayMs });
            }
        },

        async saveAnswers() {
            if (!this.answers || Object.keys(this.answers).length === 0) return null;
            return this.postRecap(`${this.baseUrl}/solar_answers`, {
                jobId: this.jobId,
                answers: this.answers
            }, 'solar_answers');
        },

        onAddressInput() {
            if (typeof this.debouncedAddressSearch === 'function') {
                this.debouncedAddressSearch();
            }
        },

        async searchAddress() {
            const query = this.addressQuery.trim();
            if (query.length < 3) {
                this.addressResults = [];
                return;
            }
            this.addressSearching = true;
            try {
                const res = await this.fetchJSON(`${this.baseUrl}/solar_address_search?q=${encodeURIComponent(query)}`);
                this.addressResults = Array.isArray(res.results) ? res.results : [];
            } catch (err) {
                this.error = err.message || 'Address search failed.';
                this.addressResults = [];
            } finally {
                this.addressSearching = false;
            }
        },

        async selectAddress(udprn) {
            if (!udprn) return;
            this.loading = true;
            this.error = null;
            this.selectedAddressUdprn = udprn;
            const token = this.navToken;
            try {
                const res = await this.postRecap(`${this.baseUrl}/solar_address`, {
                    jobId: this.jobId,
                    udprn
                }, 'solar_address');
                if (token !== this.navToken) return;
                this.address = res.jobProperty || null;
                if (this.energyDemand == null) {
                    this.energyDemand = res.energyDemand?.electricAnnual_kWh ?? null;
                }
                this.go('roofMap', {}, { delayMs: this.transitionDelayMs });
            } catch (err) {
                this.error = err.message || 'Unable to save address.';
            } finally {
                this.loading = false;
            }
        },

        async ensureGoogleMaps() {
            if (window.google && window.google.maps) return;
            if (!this.mapsKey) throw new Error('Missing Google Maps key');
            if (this.googleMapsPromise) return this.googleMapsPromise;
            this.googleMapsPromise = new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(this.mapsKey)}&libraries=geometry`;
                script.async = true;
                script.defer = true;
                script.onload = resolve;
                script.onerror = reject;
                document.head.appendChild(script);
            }).finally(() => {
                this.googleMapsPromise = null;
            });
            return this.googleMapsPromise;
        },

        async fetchGoogleSolar(lat, lon) {
            return this.postRecap(`${this.baseUrl}/solar_google_solar`, { lat, lon }, 'solar_google_solar');
        },

        async ensureGoogleSolarData(lat, lon) {
            if (this.googleSolar) return this.googleSolar;
            if (this.googleSolarPromise) return this.googleSolarPromise;
            this.googleSolarPromise = this.fetchGoogleSolar(lat, lon)
                .then(res => {
                    this.googleSolar = res.data ?? null;
                    this.segments = extractSegments(this.googleSolar);
                    return this.googleSolar;
                })
                .catch(err => {
                    console.warn('google_solar_failed', err);
                    return null;
                })
                .finally(() => {
                    this.googleSolarPromise = null;
                });
            return this.googleSolarPromise;
        },

        async initRoofMap() {
            if (this.roofMapController) return;
            if (!this.address?.latitude || !this.address?.longitude) {
                this.error = 'We need a valid address to load the map.';
                return;
            }
            await this.ensureGoogleMaps();
            this.roofMapController = createRoofMapController({
                store: this,
                refs: this.$refs,
                location: { lat: this.address.latitude, lon: this.address.longitude },
                ensureGoogleSolarData: (lat, lon) => this.ensureGoogleSolarData(lat, lon)
            });
        },

        undoRoofPoint() {
            if (this.roofMapController?.undoPoint) {
                this.roofMapController.undoPoint();
            }
        },

        clearRoofDrawing() {
            if (this.roofMapController?.clearDrawing) {
                this.roofMapController.clearDrawing();
            }
        },

        async submitRoofFromMap() {
            if (!this.roofMapController) return;
            const roof = this.roofMapController.getRoofPayload();
            if (!roof) {
                this.error = 'Add at least one roof segment to continue.';
                return;
            }
            await this.submitRoof(roof);
        },

        async submitRoofManual({ area_m2, pitchDeg, azimuthDeg, electricDemand_kWh }) {
            const areaValue = Number(area_m2);
            if (!Number.isFinite(areaValue) || areaValue <= 0) {
                throw new Error('Enter a valid roof area.');
            }
            const pitchValue = Number(pitchDeg);
            const azValue = Number(azimuthDeg);
            if (!Number.isFinite(pitchValue) || !Number.isFinite(azValue)) {
                throw new Error('Enter valid pitch and azimuth values.');
            }
            const roof = {
                planes: [{ footprintArea_m2: areaValue, pitchDeg: pitchValue }],
                segments: [{
                    area_m2: areaValue,
                    pitchDeg: pitchValue,
                    azimuthDeg: azValue
                }],
                electricDemand_kWh: electricDemand_kWh ? Number(electricDemand_kWh) : undefined,
                selection: {
                    type: 'plane',
                    pitchDeg: pitchValue,
                    azimuthDeg: azValue
                },
                config: { pvgis: { enabled: true } }
            };
            await this.submitRoof(roof);
        },

        async submitRoof(roof) {
            const token = this.navToken;
            await this.saveAnswers();
            this.estimateLoading = true;
            this.error = null;
            try {
                const res = await this.runWithMinDelay(
                    this.postRecap(`${this.baseUrl}/solar_roof`, {
                        jobId: this.jobId,
                        roof
                    }, 'solar_roof'),
                    this.estimateLoadingMinMs
                );
                if (token !== this.navToken) return;
                this.roofResult = res;
                this.roofConfirmation = res.roofConfirmation?.needed ? res.roofConfirmation : null;
                if (this.roofConfirmation) {
                    this.go('confirm');
                } else {
                    this.finishToResults();
                }
            } catch (err) {
                this.error = err.message || 'Unable to build estimate.';
            } finally {
                this.estimateLoading = false;
            }
        },

        async confirmRoof(pitchDeg, azimuthDeg) {
            const pitchValue = Number(pitchDeg);
            const azValue = Number(azimuthDeg);
            if (!Number.isFinite(pitchValue) || !Number.isFinite(azValue)) {
                throw new Error('Enter valid pitch and azimuth values.');
            }
            this.loading = true;
            this.error = null;
            const token = this.navToken;
            try {
                const res = await this.postRecap(`${this.baseUrl}/solar_roof_confirm`, {
                    jobId: this.jobId,
                    pitchDeg: pitchValue,
                    azimuthDeg: azValue
                }, 'solar_roof_confirm');
                if (token !== this.navToken) return;
                this.roofResult = res;
                this.finishToResults();
            } catch (err) {
                this.error = err.message || 'Unable to confirm roof.';
            } finally {
                this.loading = false;
            }
        },

        finishToResults() {
            if (!this.jobId) return;
            const target = this.resultsPath || this.resultsUrl;
            if (target) {
                const url = preserveConsentedGoogleAdsAttribution(preserveGrowthCorrelation(target));
                url.searchParams.set('jobId', this.jobId);
                window.location.href = url.toString();
                return;
            }
            this.step = 'results';
        },

        async runWithMinDelay(promise, minMs) {
            const start = Date.now();
            const result = await promise;
            const duration = Date.now() - start;
            const minDelay = Number(minMs);
            if (Number.isFinite(minDelay) && minDelay > 0 && duration < minDelay) {
                await new Promise(resolve => setTimeout(resolve, minDelay - duration));
            }
            return result;
        },

        async ensureRecaptcha() {
            if (window.grecaptcha && grecaptcha.enterprise) return;
            if (this.recaptchaPromise) return this.recaptchaPromise;
            const existing = document.querySelector('script[data-recaptcha-enterprise=\"true\"]');
            if (existing) {
                this.recaptchaPromise = new Promise((resolve, reject) => {
                    if (window.grecaptcha && grecaptcha.enterprise) return resolve();
                    existing.addEventListener('load', resolve, { once: true });
                    existing.addEventListener('error', reject, { once: true });
                }).finally(() => {
                    this.recaptchaPromise = null;
                });
                return this.recaptchaPromise;
            }
            this.recaptchaPromise = new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = `https://www.google.com/recaptcha/enterprise.js?render=${this.siteKey}`;
                script.async = true;
                script.defer = true;
                script.dataset.recaptchaEnterprise = 'true';
                script.onload = resolve;
                script.onerror = reject;
                document.head.appendChild(script);
            }).finally(() => {
                this.recaptchaPromise = null;
            });
            return this.recaptchaPromise;
        },

        async recapToken(action) {
            if (!this.siteKey) throw new Error('Missing reCAPTCHA site key');
            await this.ensureRecaptcha();
            await new Promise(r => grecaptcha.enterprise.ready(r));
            return grecaptcha.enterprise.execute(this.siteKey, { action });
        },

        async fetchJSON(url, options) {
            const res = await fetch(url, options);
            const text = await res.text();
            if (!res.ok) throw new Error(`HTTP ${res.status}: ${text}`);
            return text ? JSON.parse(text) : {};
        },

        async postRecap(url, body, action) {
            const token = await this.recapToken(action);
            return this.fetchJSON(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-recaptcha-token': token },
                body: JSON.stringify(body || {})
            });
        },

        get tariffEstimate() {
            const usage = Number(this.answers?.annualUsage_kWh ?? this.energyDemand ?? 0);
            if (!Number.isFinite(usage) || usage <= 0) return null;
            return Math.round(usage * (this.tariffPence / 100));
        }
        });

        if (typeof window !== 'undefined') {
            window.__solarJourneyState = state;
        }
        return state;
    };

    if (typeof window !== 'undefined') {
        window.solarJourney = createStore;
    }

    const initRoot = (root) => {
        if (!root) return;
        if (root.dataset.solarJourneyMounted === 'true') return;
        window.Alpine.initTree(root);
        root.dataset.solarJourneyMounted = 'true';
    };

    const refreshAlpine = () => {
        if (!window.Alpine || typeof window.Alpine.initTree !== 'function') return;
        const root = document.querySelector('[data-journey="solar-journey"]');
        if (root) initRoot(root);
    };
    const ensureMounted = () => {
        if (!window.Alpine || typeof window.Alpine.initTree !== 'function') return;
        const root = document.querySelector('[data-journey="solar-journey"]');
        if (root) {
            initRoot(root);
            return;
        }
        const observer = new MutationObserver(() => {
            const nextRoot = document.querySelector('[data-journey="solar-journey"]');
            if (!nextRoot) return;
            initRoot(nextRoot);
            observer.disconnect();
        });
        observer.observe(document.documentElement, { childList: true, subtree: true });
        setTimeout(() => observer.disconnect(), 5000);
    };

    const register = () => {
        if (!window.Alpine || typeof window.Alpine.data !== 'function') return;
        window.Alpine.data('solarJourney', createStore);
        refreshAlpine();
    };

    if (window.Alpine) {
        register();
    } else {
        document.addEventListener('alpine:init', register);
    }

    document.addEventListener('DOMContentLoaded', refreshAlpine);
    document.addEventListener('DOMContentLoaded', ensureMounted);
}

registerSolarJourney();
